import { FastField, Field } from 'formik';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTRadio, { KTRadioType } from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTImageInput from 'general/components/OtherKeenComponents/FileUpload/KTImageInput';
import CustomAccordion from 'general/components/Others/CustomAccordion';
import CustomDropdown from 'general/components/Others/CustomDropdown';
import InputSearchDropdown from 'general/components/Others/InputSearchDropdown';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import AppResource from 'general/constants/AppResource';
import Utils from 'general/utils/Utils';
import { useTranslation } from 'react-i18next';
import { Row, Col, Modal } from 'react-bootstrap';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ChargeHelper from 'features/Charge/chargeHelper';
import useRouter from 'hooks/useRouter';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import UIHelper from 'general/helpers/UIHelper';
import StringUtils from 'general/utils/StringUtils';
import agencyBankHelper from 'general/helpers/agencyBankHelper';
import ToastHelper from 'general/helpers/ToastHelper';

function AgencyInfoView({
    formikProps,
    agencyRole,
    isAdminAgency,
    agencyDetail,
    parentAgencyDetail,
    isAddSubAgency,
    adminRole,
    selectedAreaProvince,
    setSelectedAreaProvince,
    selectedAreaDistrict,
    setSelectedAreaDistrict,
    bankList,
    imageSrc,
    handleSetImage,
    isAgencyInfo,
    readyToValidData,
}) {
    const { t } = useTranslation();
    const router = useRouter();
    const agencyId = router.query?.agencyId;
    const { user } = useSelector((state) => state.auth);
    const [isAgencyIndividual, setAgencyIndividual] = useState(true);
    const [agencyLevel, setAgencyLevel] = useState(0);
    const [qBank, setQBank] = useState('');
    useEffect(() => {
        if (agencyDetail && agencyDetail?.type === AppConstants.accountType.organization) {
            setAgencyIndividual(false);
        } else setAgencyIndividual(true);
    }, [agencyDetail]);

    useEffect(() => {
        const currentLevel = agencyDetail ? agencyDetail?.deepLevel : isAddSubAgency ? parentAgencyDetail?.deepLevel + 1 : user?.agency?.deepLevel + 1;
        setAgencyLevel(currentLevel);
    }, [agencyDetail, isAddSubAgency, user, parentAgencyDetail]);

    const handleSetAgencyLevel = (value) => {
        const newLevel = value?.deepLevel + 1;
        setAgencyLevel(newLevel);
    }

    const getAgencyListOptions = () => {
        const roles = ['SALE', 'GROUP', 'INVESTOR', 'COINVESTOR'];
        return roles.map((role) => ({
            name: t(`agencyRoleDropdownTitle_${role}`),
            role: role,
        }));
    };

    // khu vuc
    const areaTree = useSelector((state) => state.app.areaTree);
    const areaProvinceList = useMemo(() => {
        return ChargeHelper.Utils.getAreaListByType([areaTree], 'P');
    }, [areaTree]);
    const areaProvinceOptions = useMemo(() => {
        return _.map(areaProvinceList, (item) => {
            return {
                name: item.name,
                value: item.areaId,
            };
        });
    }, [areaProvinceList]);

    const areaDistrictList = useMemo(() => {
        if (!selectedAreaProvince) return [];
        return ChargeHelper.Utils.getSubAreaListByParentId([areaTree], selectedAreaProvince?.areaId);
    }, [areaTree, selectedAreaProvince]);
    const areaDistrictOptions = useMemo(() => {
        return _.map(areaDistrictList, (item) => {
            return {
                name: item.name,
                value: item.areaId,
            };
        });
    }, [areaDistrictList]);

    useEffect(() => {
        if (agencyDetail) {
            const areaId = agencyDetail.areaId;
            const area = ChargeHelper.Utils.getAreaById([areaTree], areaId);
            const parentAreaId = area?.parentId;
            const parentArea = ChargeHelper.Utils.getAreaById([areaTree], parentAreaId);
            if (area?.typeCode === 'W') {
                setSelectedAreaDistrict(area);
                if (parentArea?.typeCode === 'P') {
                    setSelectedAreaProvince(parentArea);
                }
            } else if (area?.typeCode === 'P') {
                setSelectedAreaProvince(area);
            }
        }
    }, [agencyDetail, areaTree]);

    const bankOptions = useMemo(() => {
        if (!bankList || !Array.isArray(bankList)) {
            return [];
        }

        const filteredOptions = qBank
            ? bankList.filter((item) => {
                const bankCode = StringUtils.removeVietnameseTones(
                    item?.bankCode ?? ''
                ).toLowerCase();
                const shortName = StringUtils.removeVietnameseTones(
                    item?.shortName ?? ''
                ).toLowerCase();
                const bankName = StringUtils.removeVietnameseTones(
                    item?.bankName ?? ''
                ).toLowerCase();
                const searchTerm = StringUtils.removeVietnameseTones(
                    qBank ?? ''
                ).toLowerCase();
                return bankCode.includes(searchTerm) || shortName.includes(searchTerm) || bankName.includes(searchTerm);
            })
            : bankList;

        return filteredOptions.map((row) => ({
            title: row?.shortName?.length ? `${row?.shortName ?? ''}, ${row?.bankCode ?? ''}` : row?.bankCode ?? '',
            subTitle: row?.bankName ?? '',
            value: row?.bankCode ?? ''
        }));
    }, [bankList, qBank]);

    const disabledEditInfo = useMemo(() => {
        return agencyDetail?.numberOfSignedContracts > 0 && !adminRole;
    }, [agencyDetail, adminRole])

    const [looking, setLooking] = useState(false);
    const handleLookupAgencyBank = async () => {
        if (looking) return;

        setLooking(true);
        const values = formikProps.values;

        const payload = Utils.transformEmptyStrings({
            bankCode: values.bankCode,
            bankAccount: values.bankAccount,
        });

        const res = await agencyBankHelper.callApiLookupAgencyBank(payload);

        if (res && _.isObject(res) && res.accountName) {
            formikProps.setFieldValue('bankBeneficiary', res.accountName);
        } else {
            ToastHelper.showError(res || 'Không tìm thấy kết quả');
        }
        setLooking(false);
    };

    return (
        <Row>
            <CustomAccordion
                title="Thông tin cơ bản"
                collapsible={false}
                contentClassName="pb-0 pt-4"
                content={
                    <div className="d-flex flex-wrap">
                        {agencyRole === 'SALE' && (
                            <>
                                <div className="d-flex gap-2 mb-2 align-items-center">
                                    <span className='font-weight-bolder' style={{ fontSize: Utils.returnFontSize(16), color: AppColors.gray[900] }}>
                                        Cấp đại lý:
                                    </span>
                                    <span className="min-w-100px">
                                        <KTLabel
                                            text={DealerHelper.getAgencyLevel(agencyLevel)}
                                            isInline={true}
                                            size={KTLabelSize.medium}
                                            additionalClassName={`label-light-primary`}
                                        />
                                    </span>
                                </div>
                                <Col md={12} className="p-0">
                                    <div className="mt-2 mb-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }} />
                                </Col>
                            </>
                        )}
                        <Col md={12} className="p-0">
                            <KTFormGroup
                                label={
                                    <span className="font-weight-bolder" style={{ color: AppColors.gray[900], fontSize: Utils.returnFontSize(16), fontWeight: 600 }}>
                                        {t(`agencyType_${agencyRole}`)}:
                                    </span>
                                }
                                inputName="type"
                                additionalLabelClassName="w-160px"
                                additionalClassName="mb-0 d-flex"
                                inputElement={
                                    <div className="radio-inline w-100">
                                        <Field name="type">
                                            {({ field, form, meta }) => (
                                                <div className="d-flex gap-5">
                                                    {!isAdminAgency && (
                                                        <KTRadio
                                                            name={field.name + 'INDIVIDUAL'}
                                                            checked={field.value === 'INDIVIDUAL'}
                                                            label={t(`accountType_${AppConstants.accountType.individual}`)}
                                                            type={KTRadioType.outline}
                                                            additionalClassName="radio-success"
                                                            onChange={() => {
                                                                form.setFieldValue(field.name, 'INDIVIDUAL');
                                                                setAgencyIndividual(true);
                                                            }}
                                                            enableCheckValid
                                                            isValid={_.isEmpty(meta.error)}
                                                            isTouched={meta.touched}
                                                            feedbackText={meta.error}
                                                            disabled={agencyDetail && !isAddSubAgency}
                                                        />
                                                    )}
                                                    <KTRadio
                                                        name={field.name + 'ORGANIZATION'}
                                                        checked={field.value === 'ORGANIZATION'}
                                                        label={t(`accountType_${AppConstants.accountType.organization}`)}
                                                        type={KTRadioType.outline}
                                                        additionalClassName="radio-success"
                                                        onChange={() => {
                                                            form.setFieldValue(field.name, 'ORGANIZATION');
                                                            setAgencyIndividual(false);
                                                        }}
                                                        enableCheckValid
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        disabled={agencyDetail && !isAddSubAgency}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                    </div>
                                }
                            />
                        </Col>
                        <Col md={12} className="p-0">
                            <div className="mt-2 mb-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }} />
                        </Col>
                        {!isAdminAgency && (
                            <Col md={6} className="pr-lg-2 pr-md-2 pr-0 pl-0">
                                <KTFormGroup
                                    isRequired
                                    label={
                                        <span
                                            className="font-weight-boldest"
                                            style={{ fontSize: 13, color: AppColors.gray[800] }}
                                        >
                                            {agencyRole !== 'INVESTOR' &&
                                                agencyRole !== 'COINVESTOR' &&
                                                agencyRole !== 'GROUP' &&
                                                agencyRole !== 'CHARGEPOINT' &&
                                                agencyRole !== 'VOUCHER_PARTNER'
                                                ? t('Đại lý quản lý')
                                                : t('Đối tượng quản lý')}
                                        </span>
                                    }
                                    inputName="parentAgencyId"
                                    inputElement={
                                        <FastField name="parentAgencyId">
                                            {({ field, form, meta }) => (
                                                <CustomDropdown
                                                    id={field.name}
                                                    toggleElement={({ showError }) => (
                                                        <button
                                                            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                    ${(agencyDetail && !isAddSubAgency) ||
                                                                    (!adminRole && user?.agency?.primaryRole !== 'SALE')
                                                                    ? 'dropdown-disabled'
                                                                    : ''
                                                                } 
                    ${showError ? 'border-invalid' : ''}`}
                                                            type="button"
                                                        >
                                                            {field.value?.name ?? 'Chọn đối tượng quản lý'}
                                                        </button>
                                                    )}
                                                    selectedValue={field.value}
                                                    dropdownMenuItems={getAgencyListOptions()}
                                                    getSubItems={async (item) => {
                                                        let exceptAgencyNum = 0
                                                        if (!item.page && agencyRole === 'SALE') {
                                                            exceptAgencyNum = await AgencyHelper.API.requestGetAgencyList(
                                                                Utils.filterParams({
                                                                    role: item?.role,
                                                                    deepLevel: 5,
                                                                    status: 'NORMAL',
                                                                    q: item?.q || '',
                                                                })
                                                            ).then((data) => data.total)
                                                        }
                                                        try {
                                                            return await AgencyHelper.API.requestGetAgencyList(
                                                                Utils.filterParams({
                                                                    role: item?.role,
                                                                    page: item?.page || 0,
                                                                    limit: 10,
                                                                    q: item?.q || '',
                                                                    status: 'NORMAL',
                                                                    deepLevel: !adminRole
                                                                        ? user?.agency?.deepLevel + 1
                                                                        : '',
                                                                })
                                                            ).then((data) => {
                                                                const filterData = data.agencies?.filter(
                                                                    (x) => x?.deepLevel !== 5
                                                                )
                                                                return {
                                                                    values:
                                                                        agencyRole === 'SALE' ? filterData : data.agencies,
                                                                    total: data.total - exceptAgencyNum,
                                                                }
                                                            })
                                                        } catch {
                                                            return { values: [], total: 0 }
                                                        }
                                                    }}
                                                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                    dropdownMenuClassName="overflow-auto w-100"
                                                    onChange={(value) => {
                                                        form.setFieldValue(field.name, value)
                                                        handleSetAgencyLevel(value)
                                                    }}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                    isDropdownForm
                                                    searchable
                                                    searchFormClassName="mx-3 my-1 search-form-border"
                                                    disabled={
                                                        (agencyDetail && !isAddSubAgency) ||
                                                        (!adminRole && user?.agency?.primaryRole !== 'SALE')
                                                    }
                                                    defaultValue={
                                                        adminRole
                                                            ? agencyRole === 'SALE'
                                                                ? { name: 'Đại lý', role: 'SALE' }
                                                                : {}
                                                            : user?.agency?.primaryRole !== 'SALE'
                                                                ? {
                                                                    name: t(`agencyRoleDropdownTitle_${agencyRole}`),
                                                                    role: agencyRole,
                                                                }
                                                                : { name: 'Đại lý', role: 'SALE' }
                                                    }
                                                    defaultItem={{
                                                        role: user?.agency?.primaryRole,
                                                        item: user?.agency,
                                                    }}
                                                    appendText="quản lý"
                                                    agencyRole={agencyRole}
                                                    hasButtonAdd
                                                    subTitleType="email"
                                                    ignoreConfirmClick
                                                />
                                            )}
                                        </FastField>
                                    }
                                />
                            </Col>
                        )}
                        {!isAgencyIndividual && (
                            <Col md={6} className={`${isAdminAgency ? 'pl-0 pr-lg-2 pr-md-2 pr-0' : 'pl-lg-2 pl-md-2 pl-0 pr-0'}`}>
                                <KTFormGroup
                                    label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800], fontSize: Utils.returnFontSize(13), fontWeight: 700 }}>{t('Tên tổ chức/doanh nghiệp')} </span>}
                                    inputName="agencyName"
                                    additionalClassName=" mb-4"
                                    inputElement={
                                        <FastField name="agencyName">
                                            {({ field, form, meta }) => (
                                                <KTFormInput
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={(value) => {
                                                        form.setFieldValue(field.name, value);
                                                        form.setFieldValue('fullname', value);
                                                    }}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    type={KTFormInputType.text}
                                                    placeholder={'Nhập tên tổ chức/doanh nghiệp'}
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                />
                                            )}
                                        </FastField>
                                    }
                                    isRequired={true}
                                />
                            </Col>
                        )}
                        {isAgencyIndividual && (
                            <Col md={6} className="pr-0 pl-lg-2 pl-md-2 pl-0">
                                <KTFormGroup
                                    label={<span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>{t('Họ và tên')} </span>}
                                    inputName="fullname"
                                    additionalClassName=" mb-4"
                                    inputElement={
                                        <FastField name="fullname">
                                            {({ field, form, meta }) => (
                                                <KTFormInput
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={(value) => {
                                                        form.setFieldValue(field.name, value);
                                                        form.setFieldValue('agencyName', value);
                                                    }}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    type={KTFormInputType.text}
                                                    placeholder={'Nhập họ và tên'}
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                    disabled={disabledEditInfo}
                                                />
                                            )}
                                        </FastField>
                                    }
                                    isRequired={true}
                                />
                            </Col>
                        )}
                        {!isAgencyIndividual && (
                            <Col md={4} className={`${isAdminAgency ? 'pl-lg-2 pl-md-2 pl-0 pr-0 ' : 'pr-lg-2 pr-md-2 pr-0 pl-0 '}`}>
                                <KTFormGroup
                                    label={<span className="font-weight-boldest" style={{ fontSize: 13, color: AppColors.gray[800] }}>{t('Mã số thuế')}</span>}
                                    inputName="taxCode"
                                    additionalClassName=" mb-4"
                                    inputElement={
                                        <FastField name="taxCode">
                                            {({ field, form, meta }) => (
                                                <KTFormInput
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    type={KTFormInputType.text}
                                                    placeholder={'Nhập mã số thuế'}
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                />
                                            )}
                                        </FastField>
                                    }
                                    isRequired={true}
                                />
                            </Col>
                        )}
                        <Col md={isAgencyIndividual ? 6 : 4} className={`${isAgencyIndividual || isAdminAgency ? 'pr-lg-2 pr-md-2 pr-0 pl-0' : 'pl-lg-2 pl-md-2 pl-0 pr-lg-2 pr-md-2 pr-0'}`}>
                            <KTFormGroup
                                label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800], fontSize: Utils.returnFontSize(13), fontWeight: 700 }}>{t('email')}</span>}
                                inputName="email"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <FastField name="email">
                                        {({ field, form, meta }) => (
                                            <KTFormInput
                                                name={field.name}
                                                value={field.value}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                type={KTFormInputType.email}
                                                placeholder={t('enterEmail')}
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                disabled={agencyDetail}
                                            />
                                        )}
                                    </FastField>
                                }
                                isRequired={true}
                            />
                        </Col>
                        <Col md={isAgencyIndividual ? 6 : 4} className={`${isAgencyIndividual || isAdminAgency ? 'pr-0 pl-lg-2 pl-md-2 pl-0' : `pl-lg-2 pl-md-2 pl-0 pr-0`}`}>
                            <KTFormGroup
                                label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800], fontSize: Utils.returnFontSize(13), fontWeight: 700 }}>{t('phone')} </span>}
                                inputName="phone"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <FastField name="phone">
                                        {({ field, form, meta }) => (
                                            <KTFormInput
                                                name={field.name}
                                                value={field.value}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                type={KTFormInputType.text}
                                                placeholder={t('enterPhone')}
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                disabled={disabledEditInfo}
                                            />
                                        )}
                                    </FastField>
                                }
                                isRequired={true}
                            />
                        </Col>
                    </div>
                }
            />

            <CustomAccordion
                className="mt-4"
                title="Thông tin khác"
                collapsible={true}
                content={
                    <div className="d-flex flex-wrap">
                        {!isAgencyIndividual && (
                            <>
                                <Col md={4} className="pl-0 pr-lg-2 pr-md-2 pr-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('Người đại diện pháp luật'), !!agencyDetail)}
                                        inputName="legalRepresentative"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <FastField name="legalRepresentative">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={'Nhập tên người đại diện'}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </FastField>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="px-lg-2 px-md-2 px-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('gender'), !!agencyDetail)}
                                        inputName="gender"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <Field name="gender">
                                                {({ field, form, meta }) => (
                                                    <KTBSDropdown
                                                        toggleElement={({ showError }) => (
                                                            <button
                                                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${showError ? 'border-invalid' : ''}`}
                                                                type="button"
                                                            >
                                                                {field?.value ? t(`gender_${field?.value}`) : 'Chọn giới tính'}
                                                            </button>
                                                        )}
                                                        selectedValue={field?.value}
                                                        dropdownMenuItems={DealerHelper.genderOptions()}
                                                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                        dropdownMenuClassName="overflow-auto w-100"
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        isDropdownForm={true}
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="pr-0 pl-lg-2 pl-md-2 pl-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('Chức vụ người đại diện'), !!agencyDetail)}
                                        inputName="legalPosition"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <FastField name="legalPosition">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={'Nhập chức vụ'}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </FastField>
                                        }
                                    />
                                </Col>
                            </>
                        )}
                        <Col md={4} className="pl-0 pr-lg-2 pr-md-2 pr-0">
                            <KTFormGroup
                                label={UIHelper.buildFormLabel(t('Quốc gia'), !!agencyDetail)}
                                inputName="country"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <Field name="country">
                                        {({ field, form, meta }) => (
                                            <KTBSDropdown
                                                toggleElement={({ showError }) => (
                                                    <button
                                                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                                                            ${disabledEditInfo ? 'disabled' : ''}
                                                            ${showError ? 'border-invalid' : ''}`}
                                                        type="button"
                                                    >
                                                        {_.find(
                                                            AgencyHelper.Data.CountryOptions,
                                                            { value: field?.value }
                                                        )?.name ?? 'Chọn quốc gia'}
                                                    </button>
                                                )}
                                                selectedValue={field?.value}
                                                dropdownMenuItems={AgencyHelper.Data.CountryOptions}
                                                dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                dropdownMenuClassName="overflow-auto w-100"
                                                onChange={(value) => {
                                                    form.setFieldValue(field.name, value);
                                                    formikProps.setFieldValue('areaId', '');
                                                    setSelectedAreaProvince(null);
                                                    setSelectedAreaDistrict(null);
                                                }}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                isDropdownForm={true}
                                                searchable={true}
                                                searchFormClassName="mx-3 mt-3 search-form-border"
                                                disabled={disabledEditInfo}
                                            />
                                        )}
                                    </Field>
                                }
                            />
                        </Col>
                        <Col md={4} className="px-lg-2 px-md-2 px-0">
                            <KTFormGroup
                                label={UIHelper.buildFormLabel(t('city'), !!agencyDetail && formikProps.values.country === 'VN')}
                                inputName="city"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <Field name="city">
                                        {({ field, form, meta }) => (
                                            <KTBSDropdown
                                                toggleElement={({ showError }) => (
                                                    <button
                                                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${!selectedAreaProvince && !!agencyDetail && formikProps.values.country === 'VN' && readyToValidData
                                                            ? 'button-invalid'
                                                            : ''
                                                            } ${(formikProps.values.country !== 'VN' || disabledEditInfo) ? 'dropdown-disabled' : ''}`}
                                                        type="button"
                                                    >
                                                        {selectedAreaProvince?.name ?? 'Chọn tỉnh/thành phố'}
                                                    </button>
                                                )}
                                                selectedValue={selectedAreaProvince?.areaId}
                                                dropdownMenuItems={areaProvinceOptions}
                                                dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                dropdownMenuClassName="overflow-auto w-100"
                                                onChange={(value) => {
                                                    setSelectedAreaProvince(_.find(areaProvinceList, (item) => item.areaId === value));
                                                    setSelectedAreaDistrict(null);
                                                    const province = _.find(areaProvinceList, (item) => item.areaId === value);
                                                    formikProps.setFieldValue('areaId', province?.areaId);
                                                }}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                isDropdownForm={true}
                                                searchable={true}
                                                searchFormClassName="mx-3 mt-3 search-form-border"
                                                onUnCheck={() => {
                                                    setSelectedAreaProvince(null);
                                                    setSelectedAreaDistrict(null);
                                                    formikProps.setFieldValue('areaId', '');
                                                }}
                                                disabled={formikProps.values.country !== 'VN' || disabledEditInfo}
                                            />
                                        )}
                                    </Field>
                                }
                            />
                        </Col>
                        <Col md={4} className="pr-0 pl-lg-2 pl-md-2 pl-0">
                            <KTFormGroup
                                label={UIHelper.buildFormLabel(t('district'), !!agencyDetail && formikProps.values.country === 'VN')}
                                inputName="district"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <Field name="district">
                                        {({ field, form, meta }) => (
                                            <KTBSDropdown
                                                toggleElement={({ showError }) => (
                                                    <button
                                                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${!selectedAreaDistrict && !!agencyDetail && formikProps.values.country === 'VN' && readyToValidData
                                                            ? 'button-invalid'
                                                            : ''
                                                            } ${(formikProps.values.country !== 'VN' || disabledEditInfo) ? 'dropdown-disabled' : ''}`}
                                                        type="button"
                                                    >
                                                        {selectedAreaDistrict?.name ?? 'Chọn phường/xã'}
                                                    </button>
                                                )}
                                                selectedValue={selectedAreaDistrict?.areaId}
                                                dropdownMenuItems={areaDistrictOptions}
                                                dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                dropdownMenuClassName="overflow-auto w-100"
                                                onChange={(value) => {
                                                    const area = _.find(areaDistrictList, (item) => item.areaId === value);
                                                    setSelectedAreaDistrict(area);
                                                    formikProps.setFieldValue('areaId', area?.areaId);
                                                }}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                isDropdownForm={true}
                                                searchable={true}
                                                searchFormClassName="mx-3 mt-3 search-form-border"
                                                onUnCheck={() => {
                                                    setSelectedAreaDistrict(null);
                                                    formikProps.setFieldValue('areaId', selectedAreaProvince?.areaId);
                                                }}
                                                disabled={formikProps.values.country !== 'VN' || disabledEditInfo}
                                            />
                                        )}
                                    </Field>
                                }
                            />
                        </Col>
                        <Col md={12} className="p-0">
                            <KTFormGroup
                                label={UIHelper.buildFormLabel(isAgencyIndividual ? formikProps?.values?.country !== 'VN' ? t('Địa chỉ cư trú') : t('Địa chỉ cụ thể') : t('Địa chỉ tổ chức/doanh nghiệp'), !!agencyDetail)}
                                inputName="address"
                                additionalClassName=" mb-4"
                                inputElement={
                                    <Field name="address">
                                        {({ field, form, meta }) => (
                                            <KTFormInput
                                                name={field.name}
                                                value={field.value}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                enableCheckValid
                                                type={KTFormInputType.text}
                                                placeholder={formikProps?.values?.country !== 'VN' ? 'Nhập địa chỉ đầy đủ tại quốc gia cư trú' : 'Nhập số nhà, tên đường, thôn/xóm'}
                                                isValid={_.isEmpty(meta.error)}
                                                isTouched={meta.touched}
                                                feedbackText={meta.error}
                                                disabled={disabledEditInfo}
                                            />
                                        )}
                                    </Field>
                                }
                            />
                        </Col>
                        {!isAdminAgency
                            && agencyRole !== AgencyConstants.AgencyRole.VOUCHER_PARTNER
                            && (
                                <>
                                    <Col md={4} className="pl-0 pr-lg-2 pr-md-2 pr-0">
                                        <KTFormGroup
                                            label={UIHelper.buildFormLabel(t('Ngân hàng'), !!agencyDetail)}
                                            inputName="bankCode"
                                            additionalClassName=" mb-4"
                                            inputElement={
                                                <Field name="bankCode">
                                                    {({ field, form, meta }) => {
                                                        const selectedBank = bankOptions?.find(bank => bank.value === field.value);
                                                        return (
                                                            <InputSearchDropdown
                                                                inputName={field.name}
                                                                selectedValue={field.value}
                                                                onSelectedValueChanged={(value) => {
                                                                    form.setFieldValue(field.name, value);
                                                                    setQBank('');
                                                                }}
                                                                items={bankOptions}
                                                                onSubmitSearch={(text) => {
                                                                    if (text?.length === 0) form.setFieldValue(field.name, '');
                                                                    setQBank(text);
                                                                }}
                                                                selectedText={selectedBank ? `${selectedBank?.subTitle} (${selectedBank?.value})` : qBank}
                                                                disabled={disabledEditInfo}
                                                                enableCheckValid={true}
                                                                isTouched={meta.touched}
                                                                isValid={_.isEmpty(meta.error)}
                                                                onBlur={() => form.setFieldTouched(field.name, true)}
                                                                placeholder='Theo tên ngân hàng, tên rút gọn'
                                                            />
                                                        )
                                                    }}
                                                </Field>
                                            }
                                        />
                                    </Col>
                                    <Col md={4} className="px-lg-2 px-md-2 px-0">
                                        <KTFormGroup
                                            label={UIHelper.buildFormLabel(t('Số tài khoản ngân hàng mặc định'), !!agencyDetail)}
                                            inputName="bankAccount"
                                            additionalClassName=" mb-4"
                                            inputElement={
                                                <Field name="bankAccount">
                                                    {({ field, form, meta }) => (
                                                        <KTFormInput
                                                            name={field.name}
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                                            enableCheckValid
                                                            type={KTFormInputType.text}
                                                            placeholder={t('Nhập số tài khoản')}
                                                            isValid={_.isEmpty(meta.error)}
                                                            isTouched={meta.touched}
                                                            feedbackText={meta.error}
                                                            disabled={disabledEditInfo}
                                                            additionalButton={(
                                                                <button
                                                                    style={{
                                                                        ...UIHelper.buildFontStyle('600', '13px', '#fff'),
                                                                        backgroundColor: AppColors.info,
                                                                        border: 'none',
                                                                        padding: '6px 16px',
                                                                        borderRadius: '6px',
                                                                        cursor: looking ? 'not-allowed' : 'pointer',
                                                                        opacity: looking ? 0.7 : 1,
                                                                        minWidth: '100px',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '8px',
                                                                    }}
                                                                    onClick={handleLookupAgencyBank}
                                                                    disabled={looking}
                                                                >
                                                                    {looking ? (
                                                                        <>
                                                                            Tra cứu
                                                                            <span className="spinner spinner-white mr-4"></span>
                                                                        </>
                                                                    ) : (
                                                                        'Tra cứu'
                                                                    )}
                                                                </button>
                                                            )}
                                                        />
                                                    )}
                                                </Field>
                                            }
                                        />
                                    </Col>
                                    <Col md={4} className="pl-lg-2 pl-md-2 pl-0 pr-0">
                                        <KTFormGroup
                                            label={UIHelper.buildFormLabel(t('Tên chủ tài khoản ngân hàng'), !!agencyDetail)}
                                            inputName="bankBeneficiary"
                                            additionalClassName="mb-4"
                                            inputElement={
                                                <FastField name="bankBeneficiary">
                                                    {({ field, form, meta }) => (
                                                        <KTFormInput
                                                            name={field.name}
                                                            value={field.value}
                                                            onChange={(value) => {
                                                                let filteredValue = StringUtils.removeVietnameseTones(value);
                                                                filteredValue = filteredValue.toUpperCase();
                                                                filteredValue = filteredValue.replace(/[^A-Z0-9 ,.-]/g, '');
                                                                form.setFieldValue(field.name, filteredValue);
                                                            }}
                                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                                            enableCheckValid
                                                            type={KTFormInputType.text}
                                                            placeholder={t('Nhập họ và tên')}
                                                            isValid={_.isEmpty(meta.error)}
                                                            isTouched={meta.touched}
                                                            feedbackText={meta.error}
                                                            disabled={disabledEditInfo}
                                                        />
                                                    )}
                                                </FastField>
                                            }
                                        />
                                    </Col>
                                </>
                            )}
                        {isAgencyIndividual && (
                            <>
                                <Col md={4} className="pl-0 pr-lg-2 pr-md-2 pr-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('Ngày sinh'), !!agencyDetail)}
                                        inputName="dob"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <FastField name="dob">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.date}
                                                        placeholder={'DD/MM/YYYY'}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        maxTime={new Date().toISOString()}
                                                        rows={5}
                                                        additionalInputClassName="pr-3"
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </FastField>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="px-lg-2 px-md-2 px-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('gender'), !!agencyDetail)}
                                        inputName="gender"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <Field name="gender">
                                                {({ field, form, meta }) => (
                                                    <KTBSDropdown
                                                        toggleElement={({ showError }) => (
                                                            <button
                                                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                                                                    ${disabledEditInfo ? 'disabled' : ''}
                                                                    ${showError ? 'border-invalid' : ''}`}
                                                                type="button"
                                                            >
                                                                {field?.value ? t(`gender_${field?.value}`) : 'Chọn giới tính'}
                                                            </button>
                                                        )}
                                                        selectedValue={field?.value}
                                                        dropdownMenuItems={DealerHelper.genderOptions()}
                                                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                                                        dropdownMenuClassName="overflow-auto w-100"
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        isDropdownForm={true}
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col>
                            </>
                        )}
                        {isAgencyIndividual && (
                            <Col md={4} className="pr-0 pl-lg-2 pl-md-2 pl-0">
                                <KTFormGroup
                                    label={UIHelper.buildFormLabel(t('Mã số thuế cá nhân'))}
                                    inputName="taxCode"
                                    additionalClassName=" mb-4"
                                    inputElement={
                                        <FastField name="taxCode">
                                            {({ field, form, meta }) => (
                                                <KTFormInput
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    enableCheckValid
                                                    type={KTFormInputType.text}
                                                    placeholder={'Nhập mã số thuế'}
                                                    isValid={_.isEmpty(meta.error)}
                                                    isTouched={meta.touched}
                                                    feedbackText={meta.error}
                                                />
                                            )}
                                        </FastField>
                                    }
                                />
                            </Col>
                        )}
                        {(adminRole || isAgencyInfo)
                            && agencyRole !== AgencyConstants.AgencyRole.CHARGEPOINT
                            && agencyRole !== AgencyConstants.AgencyRole.VOUCHER_PARTNER
                            && (
                                <>
                                    <Col md={12} className="p-0">
                                        <div className="mt-2 mb-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }} />
                                    </Col>
                                    <Col md={12} className='p-0'>
                                        <Modal.Title className="mb-3">
                                            <span
                                                className="font-weight-bolder"
                                                style={{ fontSize: 16, color: AppColors.gray[900] }}
                                            >
                                                Thông tin chữ ký số
                                            </span>
                                        </Modal.Title>
                                    </Col>
                                    <Col md={6} className="pr-lg-2 pr-md-2 pr-0 pl-0">
                                        <KTFormGroup
                                            label={UIHelper.buildFormLabel(t('Số thỏa thuận (Agreement UUID)'))}
                                            inputName="hsmAgreementUUID"
                                            additionalClassName=" mb-4"
                                            inputElement={
                                                <Field name="hsmAgreementUUID">
                                                    {({ field, form, meta }) => (
                                                        <KTFormInput
                                                            name={field.name}
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                                            enableCheckValid
                                                            type={KTFormInputType.text}
                                                            placeholder={t('Nhập số thỏa thuận')}
                                                            isValid={_.isEmpty(meta.error)}
                                                            isTouched={meta.touched}
                                                            feedbackText={meta.error}
                                                        />
                                                    )}
                                                </Field>
                                            }
                                            labelAdditionalComponent={
                                                <KTTooltip
                                                    text={`<div><span>Dùng để kích hoạt tính năng ký số ứng với hợp đồng và các văn bản điện tử.</span></div>`}
                                                    textHeader="Số thỏa thuận (Agreement UUID)"
                                                    isHtmlContent
                                                >
                                                    <i
                                                        className="fa-regular fa-info-circle"
                                                        style={{ color: AppColors.info }}
                                                    />
                                                </KTTooltip>
                                            }
                                            justifyBetween={false}
                                            additionalLabelClassName="mr-2"
                                        />
                                    </Col>
                                    {/* <Col md={6} className="pl-lg-2 pl-md-2 pl-0 pr-0">
                                    <KTFormGroup
                                        label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800], fontSize: Utils.returnFontSize(13), fontWeight: 700 }}>{t('Mật khẩu HSM')}</span>}
                                        inputName="hsmPasscode"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <Field name="hsmPasscode">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={t('Nhập mật khẩu')}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col> */}
                                </>
                            )}
                        <Col md={12} className="p-0">
                            <div className="mt-2 mb-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }} />
                        </Col>
                        {isAgencyIndividual && (
                            <>
                                <Col md={12} className="p-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('Loại giấy tờ định danh:'), !!agencyDetail)}
                                        inputName="idCardType"
                                        additionalLabelClassName="w-200px"
                                        additionalClassName="mb-2 d-flex"
                                        inputElement={
                                            <div className="radio-inline w-100">
                                                <FastField name="idCardType">
                                                    {({ field, form, meta }) => (
                                                        <div className="d-flex gap-5">
                                                            <KTRadio
                                                                name={field.name + 'CC'}
                                                                checked={field.value === 'CC'}
                                                                label={t(`idCardType_${AppConstants.idCardType.cc}`)}
                                                                type={KTRadioType.outline}
                                                                additionalClassName="radio-success"
                                                                onChange={() => form.setFieldValue(field.name, 'CC')}
                                                                enableCheckValid
                                                                isValid={_.isEmpty(meta.error)}
                                                                isTouched={meta.touched}
                                                                feedbackText={meta.error}
                                                                disabled={disabledEditInfo}
                                                            />
                                                            <KTRadio
                                                                name={field.name + 'HC'}
                                                                checked={field.value === 'HC'}
                                                                label={t(`idCardType_${AppConstants.idCardType.hc}`)}
                                                                type={KTRadioType.outline}
                                                                additionalClassName="radio-success"
                                                                onChange={() => {
                                                                    form.setFieldValue(field.name, 'HC')
                                                                    form.setFieldValue('idCardPlaceOfIssue', '')
                                                                }}
                                                                enableCheckValid
                                                                isValid={_.isEmpty(meta.error)}
                                                                isTouched={meta.touched}
                                                                feedbackText={meta.error}
                                                                disabled={disabledEditInfo}
                                                            />
                                                        </div>
                                                    )}
                                                </FastField>
                                            </div>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="pr-lg-2 pr-md-2 pr-0 pl-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(formikProps.values.idCardType === 'CC' ? t('Số CCCD') : t('Số hộ chiếu'), !!agencyDetail)}
                                        inputName="idCardNumber"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <Field name="idCardNumber">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={formikProps.values.idCardType === 'CC' ? t('Nhập số CCCD') : t('Nhập số hộ chiếu')}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="px-lg-2 px-md-2 px-0">
                                    <KTFormGroup
                                        label={UIHelper.buildFormLabel(t('Ngày cấp'), !!agencyDetail)}
                                        inputName="idCardIssueDate"
                                        additionalClassName=" mb-4"
                                        inputElement={
                                            <FastField name="idCardIssueDate">
                                                {({ field, form, meta }) => (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        enableCheckValid
                                                        type={KTFormInputType.date}
                                                        placeholder={'DD/MM/YYYY'}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        maxTime={new Date().toISOString()}
                                                        rows={5}
                                                        additionalInputClassName="pr-3"
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </FastField>
                                        }
                                    />
                                </Col>
                                {formikProps.values.idCardType === 'CC' && (
                                    <Col md={4} className="pr-0 pl-lg-2 pl-md-2 pl-0">
                                        <KTFormGroup
                                            label={UIHelper.buildFormLabel(t('Nơi cấp'), !!agencyDetail)}
                                            inputName="idCardPlaceOfIssue"
                                            additionalClassName=" mb-4"
                                            inputElement={
                                                <FastField name="idCardPlaceOfIssue">
                                                    {({ field, form, meta }) => (
                                                        <KTFormInput
                                                            name={field.name}
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                                            enableCheckValid
                                                            type={KTFormInputType.text}
                                                            placeholder={'Nhập nơi cấp'}
                                                            isValid={_.isEmpty(meta.error)}
                                                            isTouched={meta.touched}
                                                            feedbackText={meta.error}
                                                            disabled={disabledEditInfo}
                                                        />
                                                    )}
                                                </FastField>
                                            }
                                        />
                                    </Col>
                                )}
                            </>
                        )}
                        {UIHelper.buildFormLabel(
                            isAgencyIndividual
                                ? formikProps.values.idCardType === 'CC'
                                    ? t('Tải lên căn cước công dân')
                                    : t('Tải lên hộ chiếu')
                                : t('Tải lên giấy phép ĐKKD'),
                            !!agencyDetail
                        )}
                        {formikProps.values.idCardType === 'CC' && isAgencyIndividual && (
                            <>
                                <Col md={4} className="pr-0 pl-0 mr-xl-4 mr-lg-4 mr-md-4 mr-0">
                                    <KTFormGroup
                                        label=""
                                        inputName={`idCardDocuments[0]`}
                                        additionalClassName="mb-3"
                                        inputElement={
                                            <Field name={`idCardDocuments[0]`}>
                                                {({ field, form, meta }) => (
                                                    <KTImageInput
                                                        fullWidth={true}
                                                        aspectRatio={'14/9'}
                                                        name={`idCardDocuments[0]`}
                                                        value={field.value ? Utils.getFullUrl(field.value) : null}
                                                        acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                                                        defaultImage={AppResource.images.evg.frontIdCard}
                                                        editable={true}
                                                        onSelectedFile={(file) => {
                                                            handleSetImage(file, 0);
                                                            const image = URL.createObjectURL(file);
                                                            form.setFieldValue(field.name, image);
                                                        }}
                                                        onRemovedFile={() => {
                                                            handleSetImage(null, 0);
                                                            form.setFieldValue(field.name, null);
                                                        }}
                                                        additionalInputClassName="h-100"
                                                        backgroundSize="contain"
                                                        isUploadIdCard={true}
                                                        uploadContent="Mặt trước"
                                                        enableCheckValid={readyToValidData && !!agencyDetail}
                                                        isValid={!!imageSrc?.[0]}
                                                        isTouched={readyToValidData}
                                                        feedbackText="Vui lòng tải lên CCCD"
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col>
                                <Col md={4} className="pr-0 pl-0">
                                    <KTFormGroup
                                        label=""
                                        inputName={`idCardDocuments[1]`}
                                        additionalClassName="mb-3"
                                        inputElement={
                                            <Field name={`idCardDocuments[1]`}>
                                                {({ field, form, meta }) => (
                                                    <KTImageInput
                                                        fullWidth={true}
                                                        aspectRatio={'14/9'}
                                                        name={`idCardDocuments[1]`}
                                                        value={field.value ? Utils.getFullUrl(field.value) : null}
                                                        acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                                                        defaultImage={AppResource.images.evg.backIdCard}
                                                        editable={true}
                                                        onSelectedFile={(file) => {
                                                            handleSetImage(file, 1);
                                                            const image = URL.createObjectURL(file);
                                                            form.setFieldValue(field.name, image);
                                                        }}
                                                        onRemovedFile={() => {
                                                            handleSetImage(null, 1);
                                                            form.setFieldValue(field.name, null);
                                                        }}
                                                        additionalInputClassName="h-100"
                                                        backgroundSize="contain"
                                                        isUploadIdCard={true}
                                                        uploadContent="Mặt sau"
                                                        enableCheckValid={readyToValidData && !!agencyDetail}
                                                        isValid={!!imageSrc?.[1]}
                                                        isTouched={readyToValidData}
                                                        feedbackText="Vui lòng tải lên CCCD"
                                                        disabled={disabledEditInfo}
                                                    />
                                                )}
                                            </Field>
                                        }
                                    />
                                </Col>
                            </>
                        )}
                        {formikProps.values.idCardType === 'HC' && isAgencyIndividual && (
                            <Col md={4} className="pr-0 pl-0">
                                <KTFormGroup
                                    label=""
                                    inputName={`idCardDocuments[0]`}
                                    additionalClassName="mb-3"
                                    inputElement={
                                        <FastField name={`idCardDocuments[0]`}>
                                            {({ field, form, meta }) => (
                                                <KTImageInput
                                                    fullWidth={true}
                                                    aspectRatio={'14/9'}
                                                    name={`idCardDocuments[0]`}
                                                    value={field.value ? Utils.getFullUrl(field.value) : null}
                                                    acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                                                    defaultImage={AppResource.images.evg.bgPassport}
                                                    editable={true}
                                                    onSelectedFile={(file) => {
                                                        handleSetImage(file, 0);
                                                        const image = URL.createObjectURL(file);
                                                        form.setFieldValue(field.name, image);
                                                    }}
                                                    onRemovedFile={() => {
                                                        handleSetImage(null, 0);
                                                        form.setFieldValue(field.name, null);
                                                    }}
                                                    additionalInputClassName="h-100"
                                                    backgroundSize="contain"
                                                    isUploadIdCard={true}
                                                    uploadContent="Trang đầu hộ chiếu"
                                                    enableCheckValid={readyToValidData && !!agencyDetail}
                                                    isValid={!!imageSrc?.[0]}
                                                    isTouched={readyToValidData}
                                                    feedbackText="Vui lòng tải lên HC"
                                                />
                                            )}
                                        </FastField>
                                    }
                                />
                            </Col>
                        )}
                        {!isAgencyIndividual && (
                            <Col md={4} className="pr-0 pl-0">
                                <KTFormGroup
                                    label=""
                                    inputName={`idCardDocuments[0]`}
                                    additionalClassName="mb-3"
                                    inputElement={
                                        <FastField name={`idCardDocuments[0]`}>
                                            {({ field, form, meta }) => (
                                                <KTImageInput
                                                    fullWidth={true}
                                                    aspectRatio={'14/9'}
                                                    name={`idCardDocuments[0]`}
                                                    value={field.value ? Utils.getFullUrl(field.value) : null}
                                                    acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                                                    defaultImage={AppResource.images.evg.bgBusinessLicense}
                                                    editable={true}
                                                    onSelectedFile={(file) => {
                                                        handleSetImage(file, 0);
                                                        const image = URL.createObjectURL(file);
                                                        form.setFieldValue(field.name, image);
                                                    }}
                                                    onRemovedFile={() => {
                                                        handleSetImage(null, 0);
                                                        form.setFieldValue(field.name, null);
                                                    }}
                                                    additionalInputClassName="h-100"
                                                    backgroundSize="contain"
                                                    isUploadIdCard={true}
                                                    uploadContent="Tải lên"
                                                    enableCheckValid={readyToValidData && !!agencyDetail}
                                                    isValid={!!imageSrc?.[0]}
                                                    isTouched={readyToValidData}
                                                    feedbackText="Vui lòng tải lên giấy phép ĐKKD"
                                                />
                                            )}
                                        </FastField>
                                    }
                                />
                            </Col>
                        )}
                    </div>
                }
            />
        </Row>
    );
};

export default AgencyInfoView;